import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useI18n } from '../contexts/I18nContext';
import { Button } from '../components/ui/button';
import Footer from '../components/Footer';
import { ArrowLeft, ShieldCheck, Info } from 'lucide-react';

const Section = ({ title, children }) => (
  <section className="mb-10">
    <h2 className="text-xl md:text-2xl font-black text-white mb-3 tracking-tight">{title}</h2>
    <div className="text-zinc-400 text-sm md:text-[15px] leading-relaxed space-y-3">{children}</div>
  </section>
);

export default function Privacy() {
  const navigate = useNavigate();
  const { t, language } = useI18n();
  return (
    <div className="min-h-screen bg-[#050505] text-white">
      <header className="border-b border-[#1E1E24] bg-[#0A0A0B]/80 backdrop-blur sticky top-0 z-30">
        <div className="max-w-4xl mx-auto px-6 h-16 flex items-center justify-between">
          <Button variant="ghost" size="sm" onClick={() => navigate('/')} className="text-zinc-400" data-testid="privacy-back-btn">
            <ArrowLeft className="w-4 h-4 mr-1" /> {t('back') || 'Back'}
          </Button>
          <div className="flex items-center gap-2 text-sm text-zinc-500">
            <ShieldCheck className="w-4 h-4 text-[#06B6D4]" />
            {t('lastUpdated') || 'Last updated: Jan 2026'}
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-6 py-14">
        <h1 className="text-4xl md:text-5xl font-black tracking-tight mb-3">{t('privacyPolicyTitle') || 'Privacy Policy'}</h1>
        {language !== 'en' && (t('legalTranslationsNote')) && (
          <div className="inline-flex items-start gap-2 mb-6 px-3 py-2 rounded-lg bg-[#06B6D4]/5 border border-[#06B6D4]/20 text-xs text-zinc-400">
            <Info className="w-3.5 h-3.5 text-[#06B6D4] mt-0.5 shrink-0" />
            <span>{t('legalTranslationsNote')}</span>
          </div>
        )}
        <p className="text-zinc-500 mb-12">
          This Privacy Policy describes how <strong className="text-white">PlayMold</strong> ("we", "us") collects, uses, and shares
          information when you use our web app and services. We designed PlayMold to collect the minimum data needed to run the service and to
          give you clear control over your account and content.
        </p>

        <Section title="1. Information We Collect">
          <p>We collect only what's needed to operate the service:</p>
          <ul className="list-disc pl-6 space-y-1.5">
            <li><strong className="text-white">Account data:</strong> email, display name, and a hashed password (bcrypt) when you register.</li>
            <li><strong className="text-white">Content you create:</strong> games, projects, prompts you submit to the AI Copilot, published gallery entries, and any assets you generate.</li>
            <li><strong className="text-white">Usage data:</strong> play counts, likes, remixes, and standard web logs (IP, browser, timestamps) used for security and abuse prevention.</li>
            <li><strong className="text-white">Payment data:</strong> if you subscribe, Stripe processes your payment and shares only the customer/subscription ID with us. We never see or store card numbers.</li>
          </ul>
          <p>PlayMold does <strong className="text-white">not</strong> use invasive tracking pixels, sell your data to third parties, or serve behavioral ads.</p>
        </Section>

        <Section title="2. How We Use Information">
          <ul className="list-disc pl-6 space-y-1.5">
            <li>To provide the core service — create, save, publish, and play games.</li>
            <li>To power the AI Copilot and asset generation (we send your prompt text to model providers; content of your game data is not sold).</li>
            <li>To secure the service and prevent abuse.</li>
            <li>To communicate essential service messages (rare, e.g., billing receipts).</li>
          </ul>
        </Section>

        <Section title="3. AI Processing">
          <p>
            When you use the AI Copilot or image generation, the prompt text (and any images you attach) is sent to third-party model providers
            such as OpenAI, Anthropic, or Google Gemini for processing. Providers are contractually restricted from training on this data where
            supported. We never send your account credentials, other users' content, or payment details to model providers.
          </p>
        </Section>

        <Section title="4. Payments">
          <p>
            Payments are processed by <strong className="text-white">Stripe</strong>. Stripe's privacy practices are described at{' '}
            <a href="https://stripe.com/privacy" target="_blank" rel="noreferrer" className="text-[#06B6D4] hover:underline">stripe.com/privacy</a>.
            We store only the checkout session ID, subscription ID, and status necessary to manage your subscription.
          </p>
        </Section>

        <Section title="5. Sharing">
          <p>We do not sell personal information. We share data only:</p>
          <ul className="list-disc pl-6 space-y-1.5">
            <li>With service providers strictly needed to run the app (hosting, database, Stripe, AI providers).</li>
            <li>When legally required (e.g., valid subpoena) — narrowly scoped.</li>
            <li>With your own consent (e.g., when you publish a game, its content and creator name become public in the Gallery).</li>
          </ul>
        </Section>

        <Section title="6. Data Retention">
          <p>
            Account data is kept while your account is active. You can delete your account at any time from{' '}
            <button className="text-[#06B6D4] hover:underline" onClick={() => navigate('/settings')}>Settings</button>. Deletion
            removes your projects, games, and personal data within 30 days. Anonymized usage aggregates may be retained.
          </p>
        </Section>

        <Section title="7. Your Rights (GDPR/CCPA & Google Play)">
          <ul className="list-disc pl-6 space-y-1.5">
            <li><strong className="text-white">Access & export:</strong> request a copy of your data by emailing us.</li>
            <li><strong className="text-white">Delete:</strong> use the "Delete Account" button in Settings — it is a hard delete, not soft.</li>
            <li><strong className="text-white">Rectify:</strong> update your profile in Settings at any time.</li>
            <li><strong className="text-white">Opt out of AI:</strong> simply don't use the Copilot / asset features; the rest of the app works without them.</li>
          </ul>
        </Section>

        <Section title="8. Children">
          <p>
            PlayMold is intended for users 13 and older. We do not knowingly collect personal information from children under 13. If you
            believe we have, contact us and we will delete it.
          </p>
        </Section>

        <Section title="9. Security">
          <p>
            We use HTTPS everywhere, bcrypt password hashing, JWT session tokens with signed secrets, and strict access controls on
            production data. No system is 100% secure — please use a strong, unique password.
          </p>
        </Section>

        <Section title="10. International Transfers">
          <p>
            Our infrastructure and providers may operate in multiple regions. By using PlayMold you consent to data being processed in those
            regions with equivalent safeguards.
          </p>
        </Section>

        <Section title="11. Changes">
          <p>
            We'll post any material change to this policy on this page and update the "Last updated" date. Substantial changes will be
            surfaced in-app.
          </p>
        </Section>

        <Section title="12. Contact">
          <p>
            Questions or requests? Email us at <a className="text-[#06B6D4] hover:underline" href="mailto:privacy@playmold.app">privacy@playmold.app</a>
            {' '}or use the <button className="text-[#06B6D4] hover:underline" onClick={() => navigate('/contact')}>contact form</button>.
          </p>
        </Section>
      </main>
      <Footer />
    </div>
  );
}
